import { defineEntityContract, type EntityByIdRequests, type EntityStateEvents, type QueuedChangeFailureEvents } from 'shared/channels/entity';
import type { InspectionTemplateRestoreRequest } from 'shared/domain/messages/inspectionTemplate/eventMessages';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import type { InspectionTemplateModel } from 'shared/domain/template/inspectionTemplateModel';
import type { ChecklistItemInDto, InspectionTemplateInDto } from 'shared/dtos/in/template';
import type {
  ChecklistItemOutDto,
  CreateChecklistItemsOutDto,
  InspectionTemplateOutDto,
  ReorderChecklistItemOutDto,
} from 'shared/dtos/out/template';

/** A change of a template of the project; `uniqueId` correlates its outcome event. */
interface TemplateChange {
  templateId: string;
  projectId: string;
  uniqueId: string;
}

/** A change of a checklist item of a template. */
interface ChecklistItemChange extends TemplateChange {
  checklistItemId: string;
}

/**
 * Template events. Template and checklist item changes share the outcome
 * types and are told apart only by their `uniqueId`: a create answers with
 * the created template or the created checklist items; the other outcomes
 * carry the server's answer (`true` after a restore) and stay `unknown`.
 */
export type InspectionTemplateEvents = EntityStateEvents & QueuedChangeFailureEvents & {
  [DomainMessagesTypes.allInspectionTemplates]: { hasAll?: boolean; items: InspectionTemplateModel[] };
  [DomainMessagesTypes.createdEntity]: InspectionTemplateInDto | ChecklistItemInDto[];
  [DomainMessagesTypes.updatedEntity]: unknown;
  [DomainMessagesTypes.deletedEntity]: unknown;
};

/** Template and checklist item changes; each resolves once queued, its outcome follows as an event. */
export type InspectionTemplateRequests = EntityByIdRequests<InspectionTemplateModel> & {
  create: {
    request: { inspectionTemplateOutDto: InspectionTemplateOutDto; projectId: string; uniqueId: string };
    response: void;
  };
  edit: {
    request: TemplateChange & { inspectionTemplateOutDto: Partial<InspectionTemplateOutDto> };
    response: void;
  };
  remove: { request: TemplateChange; response: void };
  restore: { request: InspectionTemplateRestoreRequest; response: void };
  createChecklistItems: {
    request: TemplateChange & { checklistItemsOutDto: CreateChecklistItemsOutDto };
    response: void;
  };
  editChecklistItem: {
    request: ChecklistItemChange & { checklistItemOutDto: ChecklistItemOutDto };
    response: void;
  };
  removeChecklistItem: { request: ChecklistItemChange; response: void };
  reorderChecklistItem: {
    request: ChecklistItemChange & { reorderDto: ReorderChecklistItemOutDto };
    response: void;
  };
};

export const inspectionTemplateContract = defineEntityContract<
  InspectionTemplateModel,
  InspectionTemplateEvents,
  InspectionTemplateRequests
>('inspectionTemplateChannel', { collectionEvent: DomainMessagesTypes.allInspectionTemplates });
